import React from 'react';
import {
  RefreshCw,
  Smartphone,
  Tablet,
  Monitor,
  Terminal,
  Package,
  Globe,
  Square,
  ExternalLink,
  Maximize2,
  Minimize2,
  Zap,
  ChevronDown,
  Pencil,
  Play,
} from 'lucide-react';
import { Button } from './Button';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import styles from './LivePreviewToolbar.module.css';

// Custom icon components for screenshot and annotation
const ScreenshotIcon = ({ size = 16 }: { size?: number }) => (
  <img
    src="/icons/live-screenshot.png"
    alt="Screenshot"
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
  />
);

const AnnotateIcon = ({ size = 16 }: { size?: number }) => (
  <img
    src="/icons/annotations.png"
    alt="Annotate"
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
  />
);

export type DeviceSize = 'mobile' | 'tablet' | 'desktop';

interface LivePreviewToolbarProps {
  deviceSize: DeviceSize;
  onDeviceSizeChange: (size: DeviceSize) => void;
  onRefresh: () => void;
  onScreenshot?: () => void;
  onAnnotate?: () => void;
  onInspect?: () => void;
  isInspectMode?: boolean;
  onPublish?: () => void;
  onInstallDependencies?: () => void;
  onToggleTerminal?: () => void;
  onStopServer?: () => void;
  onRestartServer?: () => void;
  onOpenInNewTab?: () => void;
  onToggleFullscreen?: () => void;
  previewUrl?: string | null;
  isFullscreen?: boolean;
  isLoading: boolean;
  showDeviceControls: boolean;
  showTerminalToggle: boolean;
  showInstallButton: boolean;
  showStopButton: boolean;
  isTerminalVisible: boolean;
  webContainerStatus?: string;
  // Optional automatic publish toggle control for Rust Wasm SPA sessions
  showAutoPublishToggle?: boolean;
  autoPublishEnabled?: boolean;
  onToggleAutoPublish?: () => void;
  // Current preview path (e.g., "/", "/about", "/contact-us")
  currentPath?: string;
  // Available pages for suggestions (path + displayName)
  availablePages?: { path: string; displayName: string }[];
  // Whether to show the routes dropdown (useful to hide for Next/React/Node projects)
  showRoutesDropdown?: boolean;
  // Callback when user navigates using the path input
  onNavigate?: (path: string) => void;
  className?: string;
  // Session project name shown in the toolbar so users know which session they're in
  sessionName?: string;
}

export const LivePreviewToolbar: React.FC<LivePreviewToolbarProps> = ({
  deviceSize,
  onDeviceSizeChange,
  onRefresh,
  onScreenshot,
  onAnnotate,
  onInspect,
  isInspectMode,
  onPublish,
  onInstallDependencies,
  onToggleTerminal,
  onStopServer,
  onRestartServer,
  onOpenInNewTab,
  onToggleFullscreen,
  previewUrl,
  isFullscreen,
  isLoading,
  showDeviceControls,
  showTerminalToggle,
  showInstallButton,
  showStopButton,
  isTerminalVisible,
  webContainerStatus,
  showAutoPublishToggle,
  autoPublishEnabled,
  onToggleAutoPublish,
  currentPath,
  onNavigate,
  availablePages,
  showRoutesDropdown,
  className,
  sessionName,
}) => {
  const [routesOpen, setRoutesOpen] = React.useState(false);

  const handleNavigate = (path: string) => {
    const normalized = path && path.length > 0 ? (path.startsWith('/') ? path : `/${path}`) : '/';
    if (onNavigate) onNavigate(normalized);
    setRoutesOpen(false);
  };

  // Find display name for current path
  const currentDisplayName = React.useMemo(() => {
    if (!availablePages || !currentPath) return 'Home';
    const match = availablePages.find((p) => p.path === currentPath);
    return match ? match.displayName : currentPath;
  }, [availablePages, currentPath]);

  return (
    <div className={`${styles.toolbar} ${className || ''}`}>
      <div className={styles.left}>
        {/* Session project name — helps user know which project is previewing */}
        {sessionName && (
          <span className={styles.sessionNameBadge} title={sessionName}>
            {sessionName}
          </span>
        )}
        {/* Screenshot & Annotation buttons */}
        {(onScreenshot || onAnnotate) && (
          <div className={styles.screenshotActions}>
            {onScreenshot && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" onClick={onScreenshot}>
                    <ScreenshotIcon size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Take Screenshot</TooltipContent>
              </Tooltip>
            )}
            {onAnnotate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon-sm" onClick={onAnnotate}>
                    <AnnotateIcon size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Annotate Screenshot</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        {/* Visual editor — always visible, including mobile */}
        {onInspect && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onInspect}
                style={
                  isInspectMode
                    ? { color: '#C9822A', background: 'rgba(201,130,42,0.12)' }
                    : undefined
                }
              >
                <Pencil size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isInspectMode ? 'Cancel inspect (ESC)' : 'Visual Editor'}
            </TooltipContent>
          </Tooltip>
        )}

        {showDeviceControls && (
          <div className={styles.deviceControls}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={deviceSize === 'mobile' ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={() => onDeviceSizeChange('mobile')}
                >
                  <Smartphone size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mobile (375px)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={deviceSize === 'tablet' ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={() => onDeviceSizeChange('tablet')}
                >
                  <Tablet size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Tablet (768px)</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={deviceSize === 'desktop' ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={() => onDeviceSizeChange('desktop')}
                >
                  <Monitor size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Desktop (100%)</TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Routes dropdown selector */}
        {availablePages && availablePages.length > 0 && (showRoutesDropdown ?? true) && (
          <div className={styles.routesSelector} style={{ position: 'relative' }}>
            <button
              className={styles.routesSelectorBtn}
              onClick={() => setRoutesOpen((v) => !v)}
              onBlur={() => setTimeout(() => setRoutesOpen(false), 150)}
            >
              <Globe size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
              <span className={styles.routesSelectorLabel}>
                {currentDisplayName}
                <span className={styles.routesSelectorPath}> ({currentPath || '/'})</span>
              </span>
              <ChevronDown size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
            </button>

            {routesOpen && (
              <div className={styles.routesDropdown} onMouseDown={(e) => e.preventDefault()}>
                {availablePages.map((p) => (
                  <div
                    key={p.path}
                    className={`${styles.routeItem} ${p.path === currentPath ? styles.routeItemActive : ''}`}
                    onClick={() => handleNavigate(p.path)}
                  >
                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{p.displayName}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                      {p.path}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className={styles.actions}>
          {showStopButton && onStopServer && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onStopServer}>
                  <Square size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Stop Server</TooltipContent>
            </Tooltip>
          )}
          {showStopButton && onRestartServer && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onRestartServer}>
                  <img src="/icons/restart.png" alt="Restart" style={{ height: 14, width: 'auto' }} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Restart Server</TooltipContent>
            </Tooltip>
          )}{' '}
          {showAutoPublishToggle &&
            typeof autoPublishEnabled === 'boolean' &&
            onToggleAutoPublish && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={autoPublishEnabled ? 'secondary' : 'ghost'}
                    size="icon-sm"
                    onClick={onToggleAutoPublish}
                  >
                    <Zap size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {autoPublishEnabled ? 'Auto-publish: On' : 'Auto-publish: Off'}
                </TooltipContent>
              </Tooltip>
            )}
          {showTerminalToggle && onToggleTerminal && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isTerminalVisible ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={onToggleTerminal}
                >
                  <Terminal size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle Terminal</TooltipContent>
            </Tooltip>
          )}
          {showInstallButton && onInstallDependencies && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onInstallDependencies}>
                  <Package size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Install packages</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={onRefresh}>
                <RefreshCw size={14} className={isLoading ? styles.spin : ''} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh Preview</TooltipContent>
          </Tooltip>
          {onPublish && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" onClick={onPublish}>
                  <Globe size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Publish</TooltipContent>
            </Tooltip>
          )}
          {(previewUrl || onOpenInNewTab) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    if (onOpenInNewTab) {
                      onOpenInNewTab();
                    } else if (previewUrl) {
                      window.open(previewUrl, '_blank');
                    }
                  }}
                >
                  <ExternalLink size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open in New Tab</TooltipContent>
            </Tooltip>
          )}
          {onToggleFullscreen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isFullscreen ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={onToggleFullscreen}
                >
                  {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
